import { useEffect } from "react";

type SeoProps = {
  title: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  jsonLd?: object | object[];
};

const BASE_URL = "https://comixn.github.io";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * 统一 SEO 组件：负责更新页面标题、meta 描述/关键词、Canonical、
 * Open Graph 与 Twitter Card，并注入 Schema.org JSON-LD 结构化数据。
 */
export default function Seo({ title, description = "", keywords = "", canonicalPath = "/", jsonLd }: SeoProps) {
  useEffect(() => {
    const fullTitle = title ? `${title} - 漫画岛同好讨论社区` : "漫画岛 - 漫画同好讨论社区";
    document.title = fullTitle;

    const desc =
      description ||
      "漫画岛是一个面向漫画爱好者的同好讨论社区，聚合漫画评论区、粉丝圈子与书评交流。安利热门漫画、阅读深度书评、参与追更讨论，找同好一起追更。";
    upsertMeta("name", "description", desc);
    upsertMeta("name", "keywords", keywords || "漫画岛,漫画岛同好讨论,漫画评论区,粉丝圈子,书评交流,漫画推荐");

    const canonical = `${BASE_URL}${canonicalPath}`;
    upsertMeta("name", "canonical", canonical);
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);

    // Open Graph
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "漫画岛");
    upsertMeta("property", "og:locale", "zh_CN");
    upsertMeta("property", "og:image", `${BASE_URL}/og-cover.jpg`);

    // Twitter Card
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", `${BASE_URL}/og-cover.jpg`);

    // JSON-LD
    const scriptId = "page-jsonld";
    document.getElementById(scriptId)?.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonicalPath, jsonLd]);

  return null;
}